import { type TFunction } from 'i18next'

import { type PathKey } from '~/apps/ninesegsAnalyzer/routes/paths'

import {
  type DataDisplayType,
  dataDisplayTypeLabelI18nKeyMap,
} from '../dataDisplayType'
import { type FigureInfo } from '../figureInfo'
import {
  type ResultViewerType,
  resultViewerTypeLabelI18nKeyMap,
} from '../resultViewerType'
import {
  type SegmentDisplayType,
  segmentDisplayTypeLabelI18nKeyMap,
} from '../segmentDisplayType'
import { type FilterText } from '../types'

import { toCustomerAttributeTexts } from './toCustomerAttributeTexts'
import { toQuestionTexts } from './toQuestionTexts'

type CustomerAttributeGroups = Parameters<typeof toCustomerAttributeTexts>[1]
type Questions = Parameters<typeof toQuestionTexts>[1]

type BuildFigureInfoParams = {
  brandAccountId: string
  brandGroupName?: string
  brandName?: string
  customerAttributeGroups?: CustomerAttributeGroups
  customerAttributeIds?: string[]
  dataDisplayType: DataDisplayType
  figureType?: string
  groupFilters?: FigureInfo['groupFilters']
  overlapBrand1?: string
  overlapBrand2?: string
  overlapGroupFilters?: FigureInfo['overlapGroupFilters']
  pathKey: PathKey
  questionIds?: string[]
  questions?: Questions
  resultViewerType?: ResultViewerType
  segmentDisplayType?: SegmentDisplayType
  surveyFilter?: FilterText[][]
  surveyTitle?: string
  t: TFunction
}

/**
 * 図情報生成パラメータからレポート用の図情報を構築します。
 *
 * @param params - 図情報生成パラメータ。
 * @returns 構築された図情報。
 */
export function buildFigureInfo(params: BuildFigureInfoParams): FigureInfo {
  const {
    brandAccountId,
    brandGroupName,
    brandName,
    customerAttributeGroups,
    customerAttributeIds,
    dataDisplayType,
    figureType,
    groupFilters,
    overlapBrand1,
    overlapBrand2,
    overlapGroupFilters,
    pathKey,
    questionIds,
    questions,
    resultViewerType,
    segmentDisplayType,
    surveyFilter,
    surveyTitle,
    t,
  } = params

  return {
    brandAccountId,
    pathKey,
    ...(brandGroupName !== undefined ? { brandGroup: brandGroupName } : {}),
    ...(brandName !== undefined ? { brand: brandName } : {}),
    ...(customerAttributeIds && customerAttributeGroups
      ? {
          customerAttributes: toCustomerAttributeTexts(
            customerAttributeIds,
            customerAttributeGroups,
            t,
          ),
        }
      : {}),
    dataDisplayType: dataDisplayTypeLabelI18nKeyMap[dataDisplayType],
    ...(figureType !== undefined ? { figureType } : {}),
    ...(groupFilters !== undefined ? { groupFilters } : {}),
    ...(overlapBrand1 !== undefined ? { overlapBrand1 } : {}),
    ...(overlapBrand2 !== undefined ? { overlapBrand2 } : {}),
    ...(overlapGroupFilters !== undefined ? { overlapGroupFilters } : {}),
    ...(questionIds && questions
      ? { questions: toQuestionTexts(questionIds, questions) }
      : {}),
    ...(resultViewerType !== undefined
      ? {
          resultViewerType: resultViewerTypeLabelI18nKeyMap[resultViewerType],
        }
      : {}),
    ...(segmentDisplayType !== undefined
      ? {
          segmentDisplayType:
            segmentDisplayTypeLabelI18nKeyMap[segmentDisplayType],
        }
      : {}),
    ...(surveyFilter !== undefined ? { surveyFilter } : {}),
    ...(surveyTitle !== undefined ? { survey: surveyTitle } : {}),
  }
}
